import * as i0 from '@angular/core';
import { Injectable, Inject, Pipe, NgModule, EventEmitter, Component, Input, Output, ViewEncapsulation } from '@angular/core';
import { map } from 'rxjs/operators';
import * as i1 from '@angular/cdk/layout';
import * as i1$2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { WINDOW } from '@ng-web-apis/common';
import * as i1$1 from '@angular/platform-browser';
import * as i3 from '@bloomreach/ng-sdk';
import { BrSdkModule } from '@bloomreach/ng-sdk';
import merge from 'lodash/merge';
import get from 'lodash/get';

/**
 * Frk defined breakpoints
 *
 * WARNING: these need to be kept in sync with the CSS defined breakpoints in src/scss/abstracts/_variables.scss
 */
var FrkBreakpoint;
(function (FrkBreakpoint) {
    FrkBreakpoint["Mobile"] = "(max-width: 767px)";
    FrkBreakpoint["Tablet"] = "(min-width: 768px) and (max-width: 1023px)";
    FrkBreakpoint["LargeTablet"] = "(min-width: 1024px) and (max-width: 1199px)";
    FrkBreakpoint["SmallDesktop"] = "(min-width: 1200px) and (max-width: 1599px)";
    FrkBreakpoint["Desktop"] = "(min-width: 1600px) and (max-width: 1899px)";
    FrkBreakpoint["LargeDesktop"] = "(min-width: 1900px)";
})(FrkBreakpoint || (FrkBreakpoint = {}));
/**
 * Array containing all defined Frk Breakpoints
 */
const FrkAllBreakpoints = [
    FrkBreakpoint.Mobile,
    FrkBreakpoint.Tablet,
    FrkBreakpoint.LargeTablet,
    FrkBreakpoint.SmallDesktop,
    FrkBreakpoint.Desktop,
    FrkBreakpoint.LargeDesktop,
];
/**
 * A service that provides details on the current breakpoint
 */
class ResponsiveService {
    /**
     * constructor gets injected with the Angular CDK BreakpointObserver
     */
    constructor(breakpointObserver) {
        this.breakpointObserver = breakpointObserver;
    }
    /**
     * Returns the current breakpoint
     */
    getBreakpoint() {
        return FrkAllBreakpoints.find((bp) => this.breakpointObserver.isMatched(bp));
    }
    /**
     * Returns an observable stream that fires every time the breakpoint changes
     */
    getBreakpointObs$() {
        return this.breakpointObserver
            .observe(FrkAllBreakpoints)
            .pipe(map((val) => FrkAllBreakpoints.find((bp) => val.breakpoints[bp])));
    }
    /**
     * Returns a boolean observable stream that fires when the breakpoint changes to handheld (mobile or tablet)
     */
    isHandheld$() {
        return this.breakpointObserver.observe(FrkAllBreakpoints).pipe(map(
        // return true if current breakpoint is Mobile or Tablet
        (val) => val.breakpoints[FrkBreakpoint.Mobile] ||
            val.breakpoints[FrkBreakpoint.LargeTablet] ||
            val.breakpoints[FrkBreakpoint.Tablet]));
    }
    /**
     * Returns a boolean observable stream that fires when the breakpoint changes to mobile and small tablet, large tablet is not included
     */
    isSmallHandheld$() {
        return this.breakpointObserver.observe(FrkAllBreakpoints).pipe(map(
        // return true if current breakpoint is Mobile or Tablet
        (val) => val.breakpoints[FrkBreakpoint.Mobile] ||
            val.breakpoints[FrkBreakpoint.Tablet]));
    }
    /**
     * Returns a boolean observable stream that fires when the breakpoint changes to handheld (mobile only)
     */
    isMobile$() {
        return this.breakpointObserver.observe(FrkAllBreakpoints).pipe(map(
        // return true if current breakpoint is Mobile
        (val) => val.breakpoints[FrkBreakpoint.Mobile]));
    }
    /**
     * Returns a boolean observable stream that fires when the breakpoint changes to handheld (tablet only)
     */
    isTablet$() {
        return this.breakpointObserver.observe(FrkAllBreakpoints).pipe(map(
        // return true if current breakpoint is Tablet
        (val) => val.breakpoints[FrkBreakpoint.LargeTablet] ||
            val.breakpoints[FrkBreakpoint.Tablet]));
    }
    /**
     * Returns a boolean observable stream that fires when the target breakpoint matches or is lower
     */
    isBreakpointOrBelow$(targetBP) {
        return this.getBreakpointObs$().pipe(map(
        // return true if currentBP matches or is below targetBP
        (currentBP) => FrkAllBreakpoints.indexOf(currentBP) <=
            FrkAllBreakpoints.indexOf(targetBP)));
    }
}
ResponsiveService.ɵfac = function ResponsiveService_Factory(t) { return new (t || ResponsiveService)(i0.ɵɵinject(i1.BreakpointObserver)); };
ResponsiveService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ResponsiveService, factory: ResponsiveService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ResponsiveService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.BreakpointObserver }]; }, null); })();

class OneTrustService {
    constructor(document) {
        this.document = document;
    }
    /**
     * Trigger one trust cookie modal window.
     */
    triggerModal() {
        const oneTrustScript = this.document.createElement('script');
        oneTrustScript.type = 'text/javascript';
        oneTrustScript.id = 'one-trust-script';
        oneTrustScript.src =
            'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
        oneTrustScript.setAttribute('charset', 'UTF-8');
        oneTrustScript.setAttribute('data-document-language', 'true');
        oneTrustScript.setAttribute('data-domain-script', '2201b8e2-a7e6-4485-9b6f-89019f6483af');
        this.document.head.appendChild(oneTrustScript);
    }
}
OneTrustService.ɵfac = function OneTrustService_Factory(t) { return new (t || OneTrustService)(i0.ɵɵinject(DOCUMENT)); };
OneTrustService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: OneTrustService, factory: OneTrustService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OneTrustService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }]; }, null); })();

class ViewModeService {
    constructor(windowRef) {
        this.windowRef = windowRef;
    }
    /**
     * If SPA is loaded via an iFrame, we can assume this is being loaded
     * through the Experience Manager and therefore is in "Edit Mode".
     *
     * As of version 15 - Page.isPreview() returns true for content-preview.ftsites.com
     *
     * @returns editMode - a boolean.
     */
    isEditMode() {
        // For unit tests condition should not be checked if location contains localhost and context.html
        if (!this.windowRef.location.href.includes('localhost:9876') &&
            !this.windowRef.location.href.includes('context.html')) {
            return this.windowRef.location !== this.windowRef.parent.location;
        }
        return false;
    }
}
ViewModeService.ɵfac = function ViewModeService_Factory(t) { return new (t || ViewModeService)(i0.ɵɵinject(WINDOW)); };
ViewModeService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ViewModeService, factory: ViewModeService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ViewModeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: Window, decorators: [{
                type: Inject,
                args: [WINDOW]
            }] }]; }, null); })();

class LinkService {
    /**
     * Read the link compound and return the url
     * @param {Page} page - Bloomreach SDK Page object
     * @param {LinkCompound} linkCompound - LinkCompound object
     * @returns {string} The URL from the LinkCompound.
     */
    getCTALink(page, linkCompound) {
        var _a, _b;
        let url = '';
        if (linkCompound) {
            // Return document asset
            if ((_a = linkCompound.link[0]) === null || _a === void 0 ? void 0 : _a.document) {
                url = this.processDocumentLink(page, (_b = linkCompound.link[0]) === null || _b === void 0 ? void 0 : _b.document);
            }
        }
        return url;
    }
    /**
     * Process document link
     * @param {Page} page - Bloomreach SDK Page object
     * @param {string} document - document string ref
     * @returns {string} The URL from the document ref.
     */
    processDocumentLink(page, document) {
        let url;
        const content = page.getContent(document);
        url = content === null || content === void 0 ? void 0 : content.getUrl();
        return url;
    }
}
LinkService.ɵfac = function LinkService_Factory(t) { return new (t || LinkService)(); };
LinkService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LinkService, factory: LinkService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LinkService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], null, null); })();

class WidenService {
    /**
     * Retrieves the URL from a JSON string representing a Widen asset.
     *
     * @param {string} widenAsset - A JSON string containing information about the Widen asset.
     * @returns {string} The URL of the Widen asset.
     * @throws {SyntaxError} If the provided `widenAsset` string is not valid JSON.
     */
    getWidenAssetUrl(widenAsset) {
        /**
         * Parses the input JSON string to extract the URL.
         *
         * @returns {object} An object containing information about the Widen asset.
         * @property {string} url - The URL of the Widen asset.
         */
        let widenJson;
        try {
            widenJson = widenAsset ? JSON.parse(widenAsset) : { url: '' };
        }
        catch (e) {
            widenJson = { url: '' };
        }
        const url = widenJson.url;
        return url;
    }
}
WidenService.ɵfac = function WidenService_Factory(t) { return new (t || WidenService)(); };
WidenService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: WidenService, factory: WidenService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WidenService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], null, null); })();

class DashIfEmptyPipe {
    transform(val) {
        if (val === '-') {
            return '—';
        }
        return val || val === 0 ? String(val) : '—';
    }
}
DashIfEmptyPipe.ɵfac = function DashIfEmptyPipe_Factory(t) { return new (t || DashIfEmptyPipe)(); };
DashIfEmptyPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "ftAltsDashIfEmpty", type: DashIfEmptyPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DashIfEmptyPipe, [{
        type: Pipe,
        args: [{
                name: 'ftAltsDashIfEmpty',
            }]
    }], null, null); })();

class SafeHtmlPipe {
    constructor(sanitized) {
        this.sanitized = sanitized;
    }
    transform(value) {
        // REMOVE THIS PIPE BEFORE PROD
        return this.sanitized.bypassSecurityTrustHtml(value);
    }
}
SafeHtmlPipe.ɵfac = function SafeHtmlPipe_Factory(t) { return new (t || SafeHtmlPipe)(i0.ɵɵdirectiveInject(i1$1.DomSanitizer, 16)); };
SafeHtmlPipe.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "safeHtml", type: SafeHtmlPipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SafeHtmlPipe, [{
        type: Pipe,
        args: [{
                name: 'safeHtml',
            }]
    }], function () { return [{ type: i1$1.DomSanitizer }]; }, null); })();

class PipesModule {
}
PipesModule.ɵfac = function PipesModule_Factory(t) { return new (t || PipesModule)(); };
PipesModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: PipesModule });
PipesModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PipesModule, [{
        type: NgModule,
        args: [{
                declarations: [DashIfEmptyPipe, SafeHtmlPipe],
                imports: [CommonModule],
                exports: [DashIfEmptyPipe, SafeHtmlPipe],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PipesModule, { declarations: [DashIfEmptyPipe, SafeHtmlPipe], imports: [CommonModule], exports: [DashIfEmptyPipe, SafeHtmlPipe] }); })();

function ButtonComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵelementStart(1, "div");
    i0.ɵɵelementStart(2, "a", 2);
    i0.ɵɵlistener("click", function ButtonComponent_div_0_Template_a_click_2_listener($event) { i0.ɵɵrestoreView(_r2); const ctx_r1 = i0.ɵɵnextContext(); return ctx_r1.onButtonClick($event); });
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵclassMapInterpolate1("alts-block-button alts-button-", ctx_r0.variant, "");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("href", ctx_r0.link == null ? null : ctx_r0.link.link[0] == null ? null : ctx_r0.link.link[0].url, i0.ɵɵsanitizeUrl);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.link == null ? null : ctx_r0.link.displayText, " ");
} }
class ButtonComponent {
    constructor() {
        this.variant = 'v1'; // Change to enum?
        this.buttonClicked = new EventEmitter();
    }
    ngOnInit() { }
    onButtonClick(event) {
        this.buttonClicked.emit(event);
    }
}
ButtonComponent.ɵfac = function ButtonComponent_Factory(t) { return new (t || ButtonComponent)(); };
ButtonComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ButtonComponent, selectors: [["ft-alts-button"]], inputs: { link: "link", variant: "variant" }, outputs: { buttonClicked: "buttonClicked" }, decls: 1, vars: 1, consts: [["class", "alts-navigation__primary-button", 4, "ngIf"], [1, "alts-navigation__primary-button"], [1, "alts-block-button__link", 3, "href", "click"]], template: function ButtonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, ButtonComponent_div_0_Template, 4, 5, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.link == null ? null : ctx.link.link);
    } }, directives: [i1$2.NgIf], styles: [""] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ButtonComponent, [{
        type: Component,
        args: [{
                selector: 'ft-alts-button',
                templateUrl: './button.component.html',
                styleUrls: ['./button.component.scss'],
            }]
    }], function () { return []; }, { link: [{
            type: Input
        }], variant: [{
            type: Input
        }], buttonClicked: [{
            type: Output
        }] }); })();

class ButtonModule {
}
ButtonModule.ɵfac = function ButtonModule_Factory(t) { return new (t || ButtonModule)(); };
ButtonModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ButtonModule });
ButtonModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ButtonModule, [{
        type: NgModule,
        args: [{
                declarations: [ButtonComponent],
                exports: [ButtonComponent],
                imports: [CommonModule],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ButtonModule, { declarations: [ButtonComponent], imports: [CommonModule], exports: [ButtonComponent] }); })();

const _c0$2 = function (a0, a1) { return { switch__container__active: a0, "switch__container__bg-color--default": a1 }; };
class SwitchComponent {
    constructor(cdr) {
        this.cdr = cdr;
        /**
         * Default state of the switch on load
         */
        this.state = 'off';
        /**
         * Background color of the switch
         */
        this.bgColor = 'default';
        /**
         * Output event having current switch state.
         */
        this.switchState = new EventEmitter();
        this.isSwitchActive = false;
    }
    ngOnInit() {
        this.isSwitchActive = this.state === 'on' ? true : false;
    }
    /**
     * Handle switch toggle action and emit its current state.
     */
    onSwitchToggle() {
        this.isSwitchActive = !this.isSwitchActive;
        this.cdr.detectChanges();
        this.switchState.emit(this.isSwitchActive ? 'on' : 'off');
    }
}
SwitchComponent.ɵfac = function SwitchComponent_Factory(t) { return new (t || SwitchComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
SwitchComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SwitchComponent, selectors: [["ft-alts-switch"]], inputs: { offLabel: "offLabel", onLabel: "onLabel", state: "state", bgColor: "bgColor" }, outputs: { switchState: "switchState" }, decls: 8, vars: 6, consts: [[1, "switch__container", 3, "ngClass", "click"], ["type", "checkbox", "tabindex", "-1", "checked", ""], ["tabindex", "0", 1, "font-button-small", "color--ft-alts-dark-blue"], ["tabindex", "0", 1, "font-button-small", "color--ft-white"], [1, "switch__wrapper"], [1, "switch__wrapper__toggle"]], template: function SwitchComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵlistener("click", function SwitchComponent_Template_div_click_0_listener() { return ctx.onSwitchToggle(); });
        i0.ɵɵelement(1, "input", 1);
        i0.ɵɵelementStart(2, "label", 2);
        i0.ɵɵtext(3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "label", 3);
        i0.ɵɵtext(5);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(6, "div", 4);
        i0.ɵɵelement(7, "div", 5);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(3, _c0$2, ctx.isSwitchActive, ctx.bgColor === "default"));
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", ctx.offLabel, " ");
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate1(" ", ctx.onLabel, " ");
    } }, directives: [i1$2.NgClass], styles: [".switch__container[_ngcontent-%COMP%]{position:relative;display:flex;padding:0;width:max-content!important;border-radius:64px;border:1px solid var(--ft-white)}.switch__container__bg-color--default[_ngcontent-%COMP%]{background-color:var(--ft-charts-alts-dark-blue)}.switch__container[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{opacity:0;position:absolute;top:0;left:0;height:100%;width:100%;z-index:1;cursor:pointer}.switch__container[_ngcontent-%COMP%]   label[_ngcontent-%COMP%]{width:max-content;padding:8px 16px;margin:0;text-align:center;cursor:pointer;transition:color .3s;z-index:1;pointer-events:none}.switch__container__active[_ngcontent-%COMP%]   .switch__wrapper[_ngcontent-%COMP%]{transform:translate(calc(100% - 2px))}.switch__container__active[_ngcontent-%COMP%]   label[_ngcontent-%COMP%]:first-of-type{color:var(--ft-white)}.switch__container__active[_ngcontent-%COMP%]   label[_ngcontent-%COMP%]:last-of-type{color:var(--ft-alts-dark-blue)}.switch__wrapper[_ngcontent-%COMP%]{position:absolute;top:-1px;left:-1px;width:calc(50% + 2px);height:calc(100% + 2px);transform:translate(0);transition:transform .3s cubic-bezier(.77,0,.175,1);pointer-events:none}.switch__wrapper__toggle[_ngcontent-%COMP%]{border-radius:64px;height:100%;background-color:var(--ft-white)}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SwitchComponent, [{
        type: Component,
        args: [{
                selector: 'ft-alts-switch',
                templateUrl: './switch.component.html',
                styleUrls: ['./switch.component.scss'],
            }]
    }], function () { return [{ type: i0.ChangeDetectorRef }]; }, { offLabel: [{
            type: Input
        }], onLabel: [{
            type: Input
        }], state: [{
            type: Input
        }], bgColor: [{
            type: Input
        }], switchState: [{
            type: Output
        }] }); })();

class SwitchModule {
}
SwitchModule.ɵfac = function SwitchModule_Factory(t) { return new (t || SwitchModule)(); };
SwitchModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SwitchModule });
SwitchModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SwitchModule, [{
        type: NgModule,
        args: [{
                declarations: [SwitchComponent],
                imports: [CommonModule],
                exports: [SwitchComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SwitchModule, { declarations: [SwitchComponent], imports: [CommonModule], exports: [SwitchComponent] }); })();

function HtmlComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 3);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("brManageContentButton", ctx_r0.document)("relative", false);
} }
const _c0$1 = function (a0) { return { "has-edit-button": a0 }; };
class HtmlComponent {
    /**
     * Constructor
     * @param viewModeService
     */
    constructor(viewModeService) {
        this.viewModeService = viewModeService;
        this.isEditMode = false;
        this.isEditMode = this.viewModeService.isEditMode();
    }
    /**
     * Ng Onit
     */
    ngOnInit() {
        var _a, _b, _c;
        if (this.page && this.component) {
            // Read Document
            this.document = this.page.getContent((_a = this.component.getModels()) === null || _a === void 0 ? void 0 : _a.document);
            this.htmlContent = (_c = (_b = this.document) === null || _b === void 0 ? void 0 : _b.getData()) === null || _c === void 0 ? void 0 : _c.html;
        }
        else {
            this.htmlContent = this.html;
        }
    }
}
HtmlComponent.ɵfac = function HtmlComponent_Factory(t) { return new (t || HtmlComponent)(i0.ɵɵdirectiveInject(ViewModeService)); };
HtmlComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: HtmlComponent, selectors: [["ft-alts-html"]], inputs: { html: "html", page: "page", component: "component" }, decls: 4, vars: 7, consts: [[3, "ngClass"], ["documentTemplateQuery", "new-Alts-Html-document", "folderTemplateQuery", "new-Alts-Html-folder", "parameter", "document", "root", "", 3, "brManageContentButton", "relative", 4, "ngIf"], [3, "innerHTML"], ["documentTemplateQuery", "new-Alts-Html-document", "folderTemplateQuery", "new-Alts-Html-folder", "parameter", "document", "root", "", 3, "brManageContentButton", "relative"]], template: function HtmlComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, HtmlComponent_ng_container_1_Template, 1, 2, "ng-container", 1);
        i0.ɵɵelement(2, "div", 2);
        i0.ɵɵpipe(3, "safeHtml");
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(5, _c0$1, ctx.isEditMode));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isEditMode);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind1(3, 3, ctx.htmlContent), i0.ɵɵsanitizeHtml);
    } }, directives: [i1$2.NgClass, i1$2.NgIf, i3.BrManageContentButtonDirective], pipes: [SafeHtmlPipe], styles: [""], encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HtmlComponent, [{
        type: Component,
        args: [{
                selector: 'ft-alts-html',
                templateUrl: './html.component.html',
                styleUrls: ['./html.component.scss'],
                encapsulation: ViewEncapsulation.None,
            }]
    }], function () { return [{ type: ViewModeService }]; }, { html: [{
            type: Input
        }], page: [{
            type: Input
        }], component: [{
            type: Input
        }] }); })();

class HtmlModule {
}
HtmlModule.ɵfac = function HtmlModule_Factory(t) { return new (t || HtmlModule)(); };
HtmlModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: HtmlModule });
HtmlModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[BrSdkModule, CommonModule, PipesModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HtmlModule, [{
        type: NgModule,
        args: [{
                declarations: [HtmlComponent],
                imports: [BrSdkModule, CommonModule, PipesModule],
                exports: [HtmlComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(HtmlModule, { declarations: [HtmlComponent], imports: [BrSdkModule, CommonModule, PipesModule], exports: [HtmlComponent] }); })();

const _c0 = function (a0) { return { active: a0 }; };
function TabsComponent_div_0_button_1_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 2);
    i0.ɵɵlistener("click", function TabsComponent_div_0_button_1_Template_button_click_0_listener() { const restoredCtx = i0.ɵɵrestoreView(_r5); const tab_r2 = restoredCtx.$implicit; const ctx_r4 = i0.ɵɵnextContext(2); return ctx_r4.onTabClick(tab_r2); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const tab_r2 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(2, _c0, ctx_r1.activeTab === tab_r2));
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", tab_r2 == null ? null : tab_r2.label, " ");
} }
function TabsComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, TabsComponent_div_0_button_1_Template, 2, 4, "button", 1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassMapInterpolate1("alts-tabs alts-tabs--", ctx_r0.theme, "");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r0.tabs);
} }
class TabsComponent {
    constructor() {
        /**
         * Output event as tab number, when tab is clicked.
         */
        this.tabClicked = new EventEmitter();
        /**
         * Name of the tabs of type string array.
         */
        this.tabs = [];
    }
    ngOnInit() {
        this.activeTab = this.tabs[0];
    }
    /**
     * A callback method that is invoked immediately after the default change detector has checked data-bound properties
     * if at least one has changed, and before the view and content children are checked.
     * @param changes SimpleChanges object
     */
    ngOnChanges(changes) {
        this.activeTab = this.tabs[0];
    }
    /**
     * Save current tab number and emit its output event, when tab is clicked.
     * @param currentTab current active tab
     */
    onTabClick(currentTab) {
        this.activeTab = currentTab;
        this.tabClicked.emit(currentTab);
    }
}
TabsComponent.ɵfac = function TabsComponent_Factory(t) { return new (t || TabsComponent)(); };
TabsComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: TabsComponent, selectors: [["ft-alts-tabs"]], inputs: { tabs: "tabs", theme: "theme" }, outputs: { tabClicked: "tabClicked" }, features: [i0.ɵɵNgOnChangesFeature], decls: 1, vars: 1, consts: [[3, "class", 4, "ngIf"], [3, "ngClass", "click", 4, "ngFor", "ngForOf"], [3, "ngClass", "click"]], template: function TabsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, TabsComponent_div_0_Template, 2, 4, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.tabs == null ? null : ctx.tabs.length);
    } }, directives: [i1$2.NgIf, i1$2.NgForOf, i1$2.NgClass], styles: [".alts-tabs[_ngcontent-%COMP%]{display:flex;grid-gap:24px;gap:24px;overflow-x:auto;white-space:nowrap;padding-bottom:8px}.alts-tabs[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]{border:1px solid var(--ft-sky-blue);border-radius:24px;padding:16px 24px;background-color:inherit;color:var(--ft-sky-blue);transition:background-color .3s,color .3s;cursor:pointer;outline-offset:-1px;font-size:var(--fonts-button-small-font-size-small-desktop);font-family:var(--fonts-button-small-font-family);font-weight:var(--fonts-button-small-font-weight);line-height:var(--fonts-button-small-line-height)}.alts-tabs[_ngcontent-%COMP%]   button[_ngcontent-%COMP%]:hover, .alts-tabs[_ngcontent-%COMP%]   button.active[_ngcontent-%COMP%]{background-color:var(--ft-sky-blue);color:var(--ft-white)}.alts-tabs[_ngcontent-%COMP%]::-webkit-scrollbar{height:6px}.alts-tabs[_ngcontent-%COMP%]::-webkit-scrollbar-thumb{background-color:var(--ft-complementary-sky-blue-1);border-radius:3px}.alts-tabs[_ngcontent-%COMP%]::-webkit-scrollbar-track{background-color:var(--ft-alts-dark-blue)}.alts-tabs--dark[_ngcontent-%COMP%]{background-color:var(--ft-charts-alts-dark-blue)}@media (min-width: 1200px){.alts-tabs[_ngcontent-%COMP%]{max-width:970px}}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TabsComponent, [{
        type: Component,
        args: [{
                selector: 'ft-alts-tabs',
                templateUrl: './tabs.component.html',
                styleUrls: ['./tabs.component.scss'],
            }]
    }], null, { tabClicked: [{
            type: Output
        }], tabs: [{
            type: Input
        }], theme: [{
            type: Input
        }] }); })();

class TabsModule {
}
TabsModule.ɵfac = function TabsModule_Factory(t) { return new (t || TabsModule)(); };
TabsModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: TabsModule });
TabsModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TabsModule, [{
        type: NgModule,
        args: [{
                declarations: [TabsComponent],
                imports: [CommonModule],
                exports: [TabsComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(TabsModule, { declarations: [TabsComponent], imports: [CommonModule], exports: [TabsComponent] }); })();

class SectionLabelComponent {
}
SectionLabelComponent.ɵfac = function SectionLabelComponent_Factory(t) { return new (t || SectionLabelComponent)(); };
SectionLabelComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SectionLabelComponent, selectors: [["ft-alts-section-label"]], inputs: { customClass: "customClass", bulletClassColor: "bulletClassColor" }, decls: 2, vars: 6, template: function SectionLabelComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div");
        i0.ɵɵelement(1, "span");
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵclassMapInterpolate1("alts-section-label flex-center ", ctx.customClass, "");
        i0.ɵɵadvance(1);
        i0.ɵɵclassMapInterpolate1("alts-section-label__bullet flex-center ", ctx.bulletClassColor, "");
    } }, styles: [".alts-section-label[_ngcontent-%COMP%]{z-index:4;grid-gap:var(--space-xs);gap:var(--space-xs);position:absolute;top:var(--space-md);left:calc(var(--space-s) - ($bulletSize / 2))}@media (min-width: 768px){.alts-section-label[_ngcontent-%COMP%]{top:var(--space-xl);left:calc(var(--space-xs) - ($bulletSize / 2) + 12px);visibility:hidden}}@media (min-width: 1600px){.alts-section-label[_ngcontent-%COMP%]{top:var(--space-xxxl);left:calc(var(--space-xs) - ($bulletSize / 2) + 16px);visibility:visible}}.alts-section-label[_ngcontent-%COMP%]:not(.in-banner){left:calc(var(--space-s) - ($bulletSize / 2) - .5rem)}@media (min-width: 768px){.alts-section-label[_ngcontent-%COMP%]:not(.in-banner){top:var(--space-xl);left:calc(var(--space-xs) - ($bulletSize / 2) + 12px)}}@media (min-width: 1600px){.alts-section-label[_ngcontent-%COMP%]:not(.in-banner){top:var(--space-xxxl);left:calc(var(--space-xs) - ($bulletSize / 2) + 16px)}}.alts-section-label.animate[_ngcontent-%COMP%]   .alts-section-label__bullet[_ngcontent-%COMP%]{animation:pulse 1s forwards}.alts-section-label__bullet[_ngcontent-%COMP%]{width:20px;height:20px;border-radius:50%}.alts-section-label__label[_ngcontent-%COMP%]{line-height:0!important}.alts-section-label.alts-section-label__label[_ngcontent-%COMP%]{line-height:0!important}.alts-section-label.alts-section-label__top-xs[_ngcontent-%COMP%]{top:var(--space-xs)}.alts-section-label.alts-section-label__top-s[_ngcontent-%COMP%]{top:var(--space-s)}.alts-section-label.alts-section-label__top-md[_ngcontent-%COMP%]{top:var(--space-md)}.alts-section-label.alts-section-label__top-lg[_ngcontent-%COMP%]{top:var(--space-lg)}.alts-section-label.alts-section-label__top-xl[_ngcontent-%COMP%]{top:var(--space-xl)}.alts-section-label.alts-section-label__top-xxl[_ngcontent-%COMP%]{top:var(--space-xxl)}.alts-section-label.alts-section-label__top-xxxl[_ngcontent-%COMP%]{top:var(--space-xxxl)}.alts-section-label.alts-section-label__top-mg[_ngcontent-%COMP%]{top:var(--space-mg)}@keyframes pulse{0%{transform:scale(.95);box-shadow:0 0 #bfbfbfb3}70%{transform:scale(1);box-shadow:0 0 0 10px #bfbfbf00}to{transform:scale(.95);box-shadow:0 0 #bfbfbf00}}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SectionLabelComponent, [{
        type: Component,
        args: [{
                selector: 'ft-alts-section-label',
                templateUrl: './section-label.component.html',
                styleUrls: ['./section-label.component.scss']
            }]
    }], null, { customClass: [{
            type: Input
        }], bulletClassColor: [{
            type: Input
        }] }); })();

class SectionLabelModule {
}
SectionLabelModule.ɵfac = function SectionLabelModule_Factory(t) { return new (t || SectionLabelModule)(); };
SectionLabelModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SectionLabelModule });
SectionLabelModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SectionLabelModule, [{
        type: NgModule,
        args: [{
                declarations: [SectionLabelComponent],
                imports: [CommonModule],
                exports: [SectionLabelComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SectionLabelModule, { declarations: [SectionLabelComponent], imports: [CommonModule], exports: [SectionLabelComponent] }); })();

class Logger {
    constructor(name) {
        this.name = name;
        this.configure();
    }
    static setConfiguration(config) {
        Logger.configuration = config;
        Logger.loggers.forEach((logger) => logger.configure());
    }
    static getConfiguration() {
        return Logger.configuration;
    }
    /**
     * add new configurations to be merged with existing configurations
     */
    static addConfiguration(config) {
        Logger.configuration = merge(Logger.configuration, config);
        Logger.loggers.forEach((logger) => logger.configure());
    }
    static getLogger(name) {
        const logger = new Logger(name);
        Logger.loggers.set(name, logger);
        return logger;
    }
    debug(...data) {
        this.logMessage(0 /* DEBUG */, data);
    }
    willLogDebug() {
        return this.level === 0 /* DEBUG */;
    }
    info(...data) {
        this.logMessage(1 /* INFO */, data);
    }
    willLogInfo() {
        return this.level <= 1 /* INFO */;
    }
    warn(...data) {
        this.logMessage(2 /* WARN */, data);
    }
    willLogWarn() {
        return this.level <= 2 /* WARN */;
    }
    error(...data) {
        this.logMessage(3 /* ERROR */, data);
    }
    willLogError() {
        return this.level <= 3 /* ERROR */;
    }
    // added for appinsights
    exception(...data) {
        this.logMessage(3 /* ERROR */, data);
    }
    event(...data) {
        this.logMessage(4 /* EVENT */, data);
    }
    logMessage(level, data) {
        const params = data;
        if (typeof params[0] === 'string') {
            params[0] = this.name + ': ' + String(params[0]);
        }
        else {
            params.unshift(this.name);
        }
        if (this.level <= level) {
            switch (level) {
                case 0 /* DEBUG */:
                    this.outputs.forEach((output) => output.debug(...params));
                    break;
                case 1 /* INFO */:
                    this.outputs.forEach((output) => output.info(...params));
                    break;
                case 2 /* WARN */:
                    this.outputs.forEach((output) => output.warn(...params));
                    break;
                case 3 /* ERROR */:
                    this.outputs.forEach((output) => output.error(...params));
                    break;
                case 4 /* EVENT */:
                    this.outputs.forEach((output) => {
                        // check because console.event() doesn't exist
                        if (output.event) {
                            output.event(...params);
                        }
                    });
                    break;
                default: // dont log anything
            }
        }
    }
    configure() {
        this.level = Logger.configuration.loggerLevels[this.name];
        if (this.level === undefined) {
            this.level = Logger.configuration.defaultLevel;
        }
        this.outputs = Logger.configuration.loggerOutputs[this.name];
        if (this.outputs === undefined) {
            this.outputs = Logger.configuration.defaultOutputs;
        }
    }
}
// implements LoggerOutput {
Logger.configuration = {
    defaultLevel: 0 /* DEBUG */,
    defaultOutputs: [console],
    loggerLevels: {},
    loggerOutputs: {},
};
Logger.loggers = new Map();

/**
 * Return EDS SVG icon name set in menu
 * @param menuItem - menu item selected
 */
function setMenuIcon(menuItem) {
    const menuIcon = get(menuItem, `parameters['Icon']`, null);
    return menuIcon;
}
/**
 * Return external link status
 * @param menuItem menu item selected
 */
function isExternalLink(menuItem) {
    const externalLinkFlag = get(menuItem, `parameters['Leaving Site (True or False)']`, 'False');
    return externalLinkFlag && externalLinkFlag.trim().toLowerCase() === 'true';
}
/**
 * Return desktop or tablet, based on navigation item setting
 * @param menuItem menu item selected
 */
function getBreakpointType(menuItem) {
    const checkBreakpointTypeFlag = get(menuItem, `parameters['Display only in: (tablet / desktop)']`, 'False');
    let breakpointType = '';
    if (!checkBreakpointTypeFlag) {
        return '';
    }
    if (checkBreakpointTypeFlag.trim().toLowerCase() === 'tablet') {
        return 'tablet';
    }
    if (checkBreakpointTypeFlag.trim().toLowerCase() === 'desktop') {
        breakpointType = 'desktop';
    }
    return breakpointType;
}
/**
 * Return new window/tab status
 * @param menuItem menu item selected
 */
function openInNewTab(menuItem) {
    const newTabFlag = get(menuItem, `parameters['Open in New Tab (True or False)']`, 'False');
    return newTabFlag && newTabFlag.trim().toLowerCase() === 'true';
}

function getMenus(page, component, name = 'menu') {
    var _a, _b;
    return (_b = (_a = getModelFromRef(page, component, name)) === null || _a === void 0 ? void 0 : _a.model) === null || _b === void 0 ? void 0 : _b.data;
}
function getModelFromRef(page, component, name) {
    const ref = (component === null || component === void 0 ? void 0 : component.getModels()) ? component === null || component === void 0 ? void 0 : component.getModels()[name] : null;
    return ref ? page.getContent(ref) : null;
}
function getPagination(page, component, name = 'pagination') {
    var _a;
    return (_a = getModelFromRef(page, component, name)) === null || _a === void 0 ? void 0 : _a.model;
}

// Export shared services

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonComponent, ButtonModule, DashIfEmptyPipe, FrkAllBreakpoints, FrkBreakpoint, HtmlComponent, HtmlModule, LinkService, Logger, OneTrustService, PipesModule, ResponsiveService, SafeHtmlPipe, SectionLabelComponent, SectionLabelModule, SwitchComponent, SwitchModule, TabsComponent, TabsModule, ViewModeService, WidenService, getBreakpointType, getMenus, getModelFromRef, getPagination, isExternalLink, openInNewTab, setMenuIcon };
